import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BotLocale, BotMessageFeedback, ChatbotConfig, HistoryItemResponse, MessageItem } from '@shared/index';
import { waitSeconds } from '@reflact/tsutil';
import dayjs, { Dayjs } from 'dayjs';
import { firstValueFrom } from 'rxjs';
import { InactiveBotServiceService } from '@shared-angular/services/inactive-bot-service.service';
import { MessageManagerAdminService } from './message-manager-admin.service';
import { MsgProcessingService } from '@shared-angular/services/msg-processing.service';
import { PromptbarManagerService } from '@shared-angular/services/promptbar-manager.service';
import { RagLoggerService } from '@shared-angular/services/rag-logger/rag-logger.service';
import { ToastrService } from '@shared-angular/services/toastr.service';

export type AskSummaryResponse = {
  id: string,
  created: number,
  model: string,
  choices: {
    index: number,
    message: {
      role: "assistant" | "system" | "user" | "developer" | "botcard",
      content: string,
      refusal: null,
      annotations: unknown[]
    },
  }[],
  usage: {
    prompt_tokens: number,
    completion_tokens: number,
    total_tokens: number,
    completion_tokens_details: {
      reasoning_tokens: number,
      audio_tokens: number,
      accepted_prediction_tokens: number,
      rejected_prediction_tokens: number
    }
  }
}

export enum BOTEVENT {
  ServerSideThreadReady,
  WelcomeAndHistoryFinished,
  FeedbackCreateStart,
  FeedbackCreateEnd,
  FeedbackDeleteStart,
  FeedbackDeleteEnd,
  HistoryMessagesLoadStart,
  HistoryMessagesLoadEnd,
  HistoryMessagesDeleteStart,
  HistoryMessagesDeleteEnd,
  CancelThinkingStart,
  CancelThinkingEnd,
  runStarted,
  runDelta,
  runFinished,
  avatarConnected,
}

@Injectable({
  providedIn: 'root'
})
export class BotService {
  public resetBotDoneEmitter: EventEmitter<void> = new EventEmitter<void>();
  public botEvents: EventEmitter<{ type: BOTEVENT, data: unknown }> = new EventEmitter<{ type: BOTEVENT, data: unknown }>();
  public botConfig?: ChatbotConfig;

  constructor(
    public msgService: MessageManagerAdminService,
    private http: HttpClient,
    public inActiveService: InactiveBotServiceService,
    public toastr: ToastrService,
    public logger: RagLoggerService,
    public promptBar: PromptbarManagerService,
    private beforeSendInterceptor: MsgProcessingService,
    private router: Router
  ) { }

  public async scrollDown(scrollBehavior: ScrollBehavior = 'smooth') {
    const scrollSelector = '#bot-content', inputId = 'promptInput';
    await waitSeconds(0.1);
    const scrollElement = document.querySelector(scrollSelector);
    if (scrollElement) {
      scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: scrollBehavior });
    }

    await this.focusInput(inputId);
  }

  private async focusInput(inputId: string) {
    await waitSeconds(0.1);
    document.getElementById(inputId)?.focus({ preventScroll: true });
  }

  public async addStat(botId: string, stat: string): Promise<{ status: string }> {
    return await firstValueFrom(this.http.get<{ status: string }>('/api/toolbox/membot/user/addStat/' + botId + '/' + stat));
  }

  public async chat(botId: string, message: string, history: MessageItem[], bypass: boolean): Promise<{ msg: string, threadId: string } | undefined> {
    this.inActiveService.botWasActive();
    this.msgService.onUserToBot.emit(message);
    const processedMsg = await this.beforeSendInterceptor.run(message);
    if (processedMsg === null) {
      return undefined;
    }
    message = processedMsg;
    this.botEvents.emit({ type: BOTEVENT.runStarted, data: { botId, message } });
    const msg: (MessageItem & { bypass?: boolean }) = {
      content: [{
        text: message,
        type: 'text'
      }],
      role: "user"
    };
    if (bypass) {
      msg.bypass = bypass;
    }
    return await firstValueFrom(this.http.post<{ msg: string, threadId: string }>("/api/user/chat/" + botId + "/chat", { message: [msg], history }));
  }

  public async deleteHistoryMessages(botId: string, messageIds: string[]) {
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteStart, data: { botId, messageIds } });
    const result = await firstValueFrom(this.http.delete('/api/toolbox/membot/user/memory/messages/' + botId, { body: { messageIds } }));
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteEnd, data: { botId, messageIds, result } });
    return result;
  }

  public async getHistoryMessages(botId: string): Promise<{ messageHistory: HistoryItemResponse[] }> {
    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesLoadStart, data: { botId } });
    const messageHistory = await firstValueFrom(this.http.get<{ messageHistory: HistoryItemResponse[] }>('/api/toolbox/membot/user/memory/messages/' + botId));

    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesLoadEnd, data: { botId, messageHistory } });
    return messageHistory;
  }

  public async supervisorNotification(botId: string, historyItemId: string, messageName: string): Promise<void> {
    await firstValueFrom(this.http.post(`/api/toolbox/membot/user/supervisorhelprequest/${botId}/${historyItemId}`, { messageName }));
  }

  public async callWebhook(botId: string, skillId: string, data?: unknown): Promise<unknown> {
    return await firstValueFrom(this.http.post(`/api/toolbox/membot/user/callwebhook/${botId}/${skillId}`, { data }, { responseType: "text" }));
  }

  public async deleteHistoryItems(botId: string, body: { fromDate?: Dayjs, toDate?: Dayjs, }): Promise<{ status: number, deletedCount: number }> {
    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesDeleteStart, data: { botId, body } });
    const deleteResult = await firstValueFrom(this.http.post<{ status: number, deletedCount: number }>("/api/toolbox/bot/admin/deleteHistoryItems/" + botId, body));
    this.botEvents.emit({ type: BOTEVENT.HistoryMessagesDeleteEnd, data: { botId, body, deleteResult } });
    return deleteResult;
  }

  public async askSummary(botId: string, body: { prompts: HistoryItemResponse[], fromDate?: Dayjs, toDate?: Dayjs, }): Promise<AskSummaryResponse> {
    return await firstValueFrom(this.http.post<AskSummaryResponse>("/api/toolbox/bot/admin/askSummary/" + botId, body));
  }

  public async answerSupervisor(botId: string, adminMsgId: string, message: string): Promise<string> {
    return await firstValueFrom(this.http.post<string>("/api/toolbox/membot/user/supervisorAnswer/" + botId + "/" + adminMsgId, { message }));
  }

  public async getDefaultComplianceText(locale: BotLocale): Promise<string> {
    return await firstValueFrom(this.http.get('/api/toolbox/bot/admin/defaultComplianceText/' + locale, { responseType: 'text' }));
  }

  public async getHistoryInfo(botId: string): Promise<{ status: string, items: { _id: string, count: number }[] }> {
    return await firstValueFrom(this.http.get<{ status: string, items: { _id: string, count: number }[] }>("/api/toolbox/bot/admin/historyInfo/" + botId));
  }

  public async countHistoryItemsByFilter(botId: string, body: { fromDate?: Dayjs, toDate?: Dayjs }): Promise<{ status: string, count: number }> {
    return await firstValueFrom(this.http.post<{ status: string, count: number }>("/api/toolbox/bot/admin/countHistoryItemsByFilter/" + botId, body));
  }

  public async sendFeedback(feedbackId: string | undefined, feedback: Omit<BotMessageFeedback, '_id' | 'created'>) {
    this.botEvents.emit({ type: BOTEVENT.FeedbackCreateStart, data: { feedbackId, feedback } });
    const result = await firstValueFrom(this.http.post<{ status: string, feedback?: BotMessageFeedback }>('/api/toolbox/feedback/add', { feedbackId, feedback }));
    this.botEvents.emit({ type: BOTEVENT.FeedbackCreateEnd, data: { feedbackId, feedback, result } });
    return result;
  }

  public async deleteFeedback(feedbackId: string) {
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteStart, data: { feedbackId } });
    const result = await firstValueFrom(this.http.delete<{ status: string }>('/api/toolbox/feedback/' + feedbackId));
    this.botEvents.emit({ type: BOTEVENT.FeedbackDeleteEnd, data: { feedbackId, result } });
    return result;
  }

  public async getFeedback(botId: string) {
    return await firstValueFrom(this.http.get<{ status: string, feedbacks: BotMessageFeedback[] }>('/api/toolbox/feedback/' + botId));
  }

  public async getDownloadableMaterial(botId: string, fileId: string) {
    return await firstValueFrom(this.http.get('/api/toolbox/bot/user/downloadfile/' + botId + '/' + fileId, { responseType: 'arraybuffer' }));
  }

  public async getMDFile(botId: string, skillId: string) {
    return await firstValueFrom(this.http.get<{ id: string, name: string, type: string, content: string }>('/api/toolbox/bot/user/downloadmdfile/' + botId + '/' + skillId));
  }

  public async getFileInformation(botId: string, skillId: string) {
    return await firstValueFrom(this.http.get<{ name: string, type: string, id: string }>('/api/toolbox/bot/user/fileinformation/' + botId + '/' + skillId));
  }

  public async getMyFeedback(botId: string) {
    return await firstValueFrom(this.http.get<{ status: string, feedbacks: BotMessageFeedback[] }>('/api/toolbox/feedback/' + botId + "/myfeedback"));
  }

  public async sendBotThinkingCancel(botId: string, threadId: string) {
    this.botEvents.emit({ type: BOTEVENT.CancelThinkingStart, data: { botId, threadId } });
    const result = await firstValueFrom(this.http.post<{ message: string }>('/api/toolbox/membot/user/cancelthinking/' + botId, { threadId }));
    this.botEvents.emit({ type: BOTEVENT.CancelThinkingEnd, data: { botId, threadId } });
    return result;
  }


  public async resetBot(botId: string) {
    //vorher Emitten sonst fehlt begrüßung
    this.resetBotDoneEmitter.emit();
    const result = await firstValueFrom(this.http.get<{ message: string }>('/api/toolbox/membot/user/resetbot/' + botId));
    return result;
  }


  public async hasBotAccessByAdmin(id: string): Promise<boolean> {
    try {
      const result = await firstValueFrom(this.http.get<{ hasAccess: boolean }>("/api/toolbox/bot/user/canaccessasadmin/" + id));
      return result.hasAccess;
    } catch (e) {
      console.warn(e);
      return false;
    }

  }
  public async getBotConfigAsUser(id: string) {
    try {
      const botConfig = await firstValueFrom(this.http.get<ChatbotConfig>("/api/toolbox/bot/user/botconfig/" + id));
      this.botConfig = botConfig;
      return botConfig;
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  public async suggestName(botId: string, description: string): Promise<string> {
    let answer = (await firstValueFrom(this.http.post<{ name: string }>('/api/toolbox/bot/admin/suggestName', { botId, description }))).name;
    if (answer.length > 30) {
      answer = answer.substring(0, 30);
    }
    return answer;
  }

  public async suggestSkill(botId: string, description: string, functionname: string): Promise<string> {
    let answer = (await firstValueFrom(this.http.post<{ skill: string }>('/api/toolbox/bot/admin/suggestSkill', { botId, description, functionname }))).skill;
    if (answer.length > 15) {
      answer = answer.substring(0, 15);
    }
    return answer;
  }

  public getColorFromConfig(botConfig: ChatbotConfig, background: boolean = false): string {
    if (botConfig.color === 'transparent') {
      return 'transparent';
    }
    if (background) {
      return botConfig.color ? botConfig.color + '33' : '#cccccc50';
    } else {
      return botConfig.color ? botConfig.color : '#cccccc';
    }
  }

  // UI HELPER
  public getBotDeeplink(bot: ChatbotConfig): "task" | "information" {
    if (!bot.showChangeHistory) {
      return "task";
    } else {
      return "information";
    }
  }

  public async exportBotConfigZIP(botConfig: ChatbotConfig) {
    const zip = await firstValueFrom(this.http.get("/api/toolbox/bot/admin/exportaszip/" + botConfig._id, { responseType: 'arraybuffer' }));
    const fileName = botConfig.name + "_" + dayjs().format('YYYY-MM-DD') + ".zip";
    this.saveAs(new Blob([zip], { type: 'application/zip' }), fileName);
  }
  /* public async importBotConfigZIP(file: File, projectId: string) {
    console.log("upload");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);
    this.toastr.success("GENERAL", $localize`:@@import-bot-file-selected:Bot wird importiert und anschließend geöffnet`);
    const res = await firstValueFrom(this.http.post<{ status: "ok" | "error", botId?: string }>("/api/toolbox/bot/admin/importaszip", formData));
    if (res.status == "ok") {
      await this.router.navigate(['/bot-admin', res.botId, 'task']);
    } else {
      this.toastr.error("ERROR", $localize`:@@import-bot-file-error:Bot konnte nicht importiert werden`);
    }
    return res;
  } */

  public saveAs(blob: Blob, filename: string) {
    const fileURL = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.setAttribute('href', fileURL);
    element.setAttribute('download', filename);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

}
